import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BrandHeader from "../../components/BrandHeader";
import { PRODUCT, WRIST_SIZES, ORDER_STATUSES } from "../../lib/constants";
import { INTENTIONS, buildPrescription } from "../../lib/crystalPrescription";
import { fullNumerology } from "../../lib/numerology";
import { determineDominantElement, selectPanchaBeads } from "../../lib/panchaEngine";
import { ordersApi } from "../../lib/api";

const REQUIRED_LABELS = {
  fullName: "Full Name",
  dob: "Date of Birth",
  email: "Email Address",
  whatsapp: "WhatsApp Number",
  gender: "Gender",
  wristSize: "Wrist Size",
  intentions: "Healing Intention",
};

export default function NewOrder() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName: "",
    dob: "",
    email: "",
    whatsapp: "",
    gender: "",
    wristSize: "",
    intentions: [],
    notes: "",
    paymentStatus: "Pending Payment",
    paymentMethod: "",
    status: "Pending Payment",
    internalNotes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };
  const toggle = (key) => setForm((f) => ({
    ...f,
    intentions: f.intentions.includes(key) ? f.intentions.filter((i) => i !== key) : [...f.intentions, key],
  }));

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Required";
    if (!form.dob) e.dob = "Required";
    if (!form.email.trim() || !form.email.includes("@")) e.email = "Valid email required";
    if (!form.whatsapp.trim() || form.whatsapp.replace(/\D/g, "").length < 10) e.whatsapp = "Valid WhatsApp required";
    if (!form.gender) e.gender = "Required";
    if (!form.wristSize) e.wristSize = "Required";
    if (form.intentions.length === 0) e.intentions = "At least one";
    setErrors(e);
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      const missing = Object.keys(errs).map((k) => REQUIRED_LABELS[k]).join(", ");
      toast.error(`Please complete: ${missing}`);
      return;
    }
    setSubmitting(true);
    try {
      const numerology = fullNumerology(form.fullName, form.dob);
      const { dominant } = determineDominantElement(numerology.lifePath, numerology.nameNumber, numerology.zodiac);
      const panchaBeads = selectPanchaBeads(dominant);
      const prescription = buildPrescription({
        lifePath: numerology.lifePath,
        nameNumber: numerology.nameNumber,
        zodiac: numerology.zodiac,
        intentions: (form.intentions || []).slice(0, 3),
        panchaBeads,
        wristSize: form.wristSize,
      });
      const payload = {
        ...form,
        orderType: PRODUCT.id,
        numerology,
        panchaBeads,
        prescriptionCrystals: prescription,
        dominantElement: dominant,
        source: "admin",
      };
      const created = await ordersApi.create(payload);
      toast.success("Order created");
      navigate(`/admin/orders/${created.id}`);
    } catch (err) {
      toast.error("Could not create order");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="new-order-page">
      <BrandHeader adminMode />
      <div className="max-w-4xl mx-auto px-5 md:px-10 py-10">
        <div className="ph-overline">New Order</div>
        <h1 className="font-serif text-4xl text-[#2C0A5E] mt-2">Enter a WhatsApp order</h1>
        <p className="text-[#6B5E7E] mt-2">Prescription and report are generated instantly after saving. All fields marked <span className="text-[#b91c1c]">*</span> are required.</p>

        <form onSubmit={submit} className="ph-card p-6 md:p-10 mt-7 space-y-6" data-testid="new-order-form" noValidate>
          <div className="grid md:grid-cols-2 gap-5">
            <LabeledInput label="Full Name" required value={form.fullName} onChange={(v) => update("fullName", v)} testid="new-fullName" error={errors.fullName} />
            <LabeledInput label="Date of Birth" type="date" required value={form.dob} onChange={(v) => update("dob", v)} testid="new-dob" error={errors.dob} />
            <LabeledInput label="Email Address" type="email" required value={form.email} onChange={(v) => update("email", v)} testid="new-email" error={errors.email} />
            <LabeledInput label="WhatsApp Number" required value={form.whatsapp} onChange={(v) => update("whatsapp", v)} testid="new-whatsapp" error={errors.whatsapp} />
            <LabeledSelect label="Gender" required value={form.gender} onChange={(v) => update("gender", v)} testid="new-gender" error={errors.gender}
              options={[["", "Select..."], ["female", "Female"], ["male", "Male"], ["other", "Other"]]} />
            <LabeledSelect label="Wrist Size" required value={form.wristSize} onChange={(v) => update("wristSize", v)} testid="new-wristSize" error={errors.wristSize}
              options={[["", "Select..."], ...WRIST_SIZES.map((w) => [w.value, `${w.label} (${w.description})`])]} />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#2C0A5E] mb-2">Healing Intentions <span className="text-[#b91c1c]">*</span></label>
            <div className={`flex flex-wrap gap-2 p-2 rounded-xl ${errors.intentions ? "border border-[#ef4444] bg-[#fef2f2]/60" : ""}`}>
              {INTENTIONS.map((i) => (
                <button key={i.key} type="button" onClick={() => toggle(i.key)} className={`ph-chip ${form.intentions.includes(i.key) ? "ph-chip-active" : ""}`} data-testid={`new-chip-${i.key}`}>
                  {i.label}
                </button>
              ))}
            </div>
            {errors.intentions && <p className="text-xs text-[#b91c1c] mt-1">{errors.intentions}</p>}
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <LabeledSelect label="Payment status" value={form.paymentStatus} onChange={(v) => update("paymentStatus", v)} testid="new-paymentStatus"
              options={[["Pending Payment", "Pending Payment"], ["Payment Confirmed", "Payment Confirmed"], ["Refunded", "Refunded"]]} />
            <LabeledSelect label="Payment method" value={form.paymentMethod} onChange={(v) => update("paymentMethod", v)} testid="new-paymentMethod"
              options={[["", "None yet"], ["UPI", "UPI"], ["Razorpay", "Razorpay"], ["Cash", "Cash"], ["Bank Transfer", "Bank Transfer"]]} />
            <LabeledSelect label="Status" value={form.status} onChange={(v) => update("status", v)} testid="new-status"
              options={ORDER_STATUSES.map((s) => [s, s])} />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#2C0A5E] mb-2">Customer notes</label>
            <textarea rows={2} className="ph-input" value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="new-notes" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2C0A5E] mb-2">Internal notes</label>
            <textarea rows={2} className="ph-input" value={form.internalNotes} onChange={(e) => update("internalNotes", e.target.value)} data-testid="new-internalNotes" />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-[#6B5E7E]">Total: <span className="font-serif text-xl text-[#6A0DAD]">Rs {PRODUCT.price}</span></div>
            <button disabled={submitting} className="ph-btn-primary" data-testid="new-order-submit">{submitting ? "Creating..." : "Create Order"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LabeledInput({ label, required, value, onChange, type = "text", testid, error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#2C0A5E] mb-2">
        {label}{required && <span className="text-[#b91c1c] ml-1">*</span>}
      </label>
      <input
        type={type}
        className={`ph-input ${error ? "border-[#ef4444] ring-1 ring-[#ef4444]/30" : ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
      {error && <p className="text-xs text-[#b91c1c] mt-1">{error}</p>}
    </div>
  );
}

function LabeledSelect({ label, required, value, onChange, options, testid, error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#2C0A5E] mb-2">
        {label}{required && <span className="text-[#b91c1c] ml-1">*</span>}
      </label>
      <select
        className={`ph-input ${error ? "border-[#ef4444] ring-1 ring-[#ef4444]/30" : ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {error && <p className="text-xs text-[#b91c1c] mt-1">{error}</p>}
    </div>
  );
}
